import { useEffect, useRef, useState, useCallback } from 'react';

const SSE_RECONNECT_DELAY_MS = 3000;
const SSE_MAX_RECONNECT_ATTEMPTS = 20;

/**
 * Server-Sent-Events Hook fuer Live-Dashboard-Updates.
 *
 * Ersetzt die frühere WebSocket-Variante: SSE laeuft ueber regulaeres HTTP/1.1
 * und wird vom Production-Ingress sauber durchgeleitet (kein Upgrade-Handshake).
 *
 * Auth: EventSource kann keine Custom-Header senden, deshalb wird das
 * JWT als ``?token=`` Query-Param mitgeschickt. Backend akzeptiert beides.
 */
export function useDashboardSSE(enabled = true) {
  const [attendance, setAttendance] = useState(null);
  const [lastEvent, setLastEvent] = useState(null);
  const [connected, setConnected] = useState(false);
  const esRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const reconnectTimeout = useRef(null);
  const stopped = useRef(false);

  const connect = useCallback(() => {
    if (!enabled || stopped.current) return;

    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (!token) {
      // Kein Token => nicht eingeloggt; SSE wuerde 401 liefern
      return;
    }

    const url = `${backendUrl}/api/sse/dashboard?token=${encodeURIComponent(token)}`;

    let es;
    try {
      es = new EventSource(url);
    } catch (e) {
      console.error('SSE Verbindungsfehler:', e);
      return;
    }
    esRef.current = es;

    es.onopen = () => {
      setConnected(true);
      reconnectAttempts.current = 0;
    };

    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'attendance_update') {
          setAttendance(data.attendance);
          setLastEvent(data.event);
          // Event nach 5 Sekunden ausblenden
          setTimeout(() => setLastEvent(null), 5000);
        }
        // 'connected' Initial-Frame wird ignoriert; setConnected ist bereits
        // ueber onopen gesetzt.
      } catch (e) {
        console.error('SSE Parse-Fehler:', e);
      }
    };

    es.onerror = () => {
      setConnected(false);
      try { es.close(); } catch { /* noop */ }
      esRef.current = null;

      if (stopped.current) return;
      if (reconnectAttempts.current < SSE_MAX_RECONNECT_ATTEMPTS) {
        reconnectAttempts.current += 1;
        // Exponential-ish backoff (max 30s)
        const delay = Math.min(
          SSE_RECONNECT_DELAY_MS * reconnectAttempts.current,
          30000,
        );
        reconnectTimeout.current = setTimeout(connect, delay);
      } else {
        stopped.current = true;
        console.info(
          'Dashboard-Live-Updates: zu viele Reconnect-Versuche, '
          + 'falle auf 30s-Polling zurueck.',
        );
      }
    };
  }, [enabled]);

  useEffect(() => {
    stopped.current = false;
    connect();
    return () => {
      stopped.current = true;
      if (esRef.current) {
        try { esRef.current.close(); } catch { /* noop */ }
        esRef.current = null;
      }
      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current);
        reconnectTimeout.current = null;
      }
    };
  }, [connect]);

  return { attendance, lastEvent, connected };
}
